// ============================================================
// A HORDE ON THE DISPLAY, BODY BY BODY (plan design-gaps, V3; the ruling: a
// body per magnitude point to forty, then one per two). The bodies stand
// packed inside the base ring (engine/display/horde); as the horde thins, the
// ones it lost POP - a ring swelling where each stood, for half a second - and
// the rest TIGHTEN, sliding to their places for the smaller count (a CSS
// transition, on only for that half second: a camera move must not smear
// them behind their ring). An arc round the ring says what share of its whole the horde
// still has. Mid-walk the bodies flow along the route in three waves (M4).
// A screen that holds still pops nothing and slides nothing.
// ============================================================

import { useEffect, useRef, useState } from 'react';
import { hordeBodyCount, hordeLayout, magnitudeShare } from '../../engine/display/horde';
import { arcPath } from '../../engine/battlefield/gauge';
import { bodyShape } from '../../engine/battlefield/tokenShape';
import type { MarkerGeometry, ScreenXY } from './markerGeometry';
import styles from '../../styles/Display.module.css';

/** A horde's magnitude and its whole, as the decorations read them (`crowdsFor`). */
export interface HordeCount {
  magnitude: number;
  max: number;
}

/** A body's radius as a share of the ring's across; how long a lost body's pop lasts (the stylesheet's hordePop). */
const BODY_SHARE = 0.11;
const POP_MS = 500;
/** The magnitude arc's radius, as a share of the ring's. */
const ARC_OUT = 1.12;
/** The waves a horde flows and drifts in (M4's three: the classic map's three bodies are its three waves). */
const WAVES = 3;

type Unit = { x: number; z: number };

interface Props {
  id: string;
  foot: ScreenXY;
  g: MarkerGeometry;
  horde: HordeCount;
  fill: string;
  title: string;
  /** This screen plays no motion: nothing pops, slides or drifts. */
  still: boolean;
  /** Mid-walk: each wave's offset along the route this frame. */
  shift?: ScreenXY[];
}

/** Which wave body `i` of `count` walks in: the first third of the layout (its middle) leads. */
const waveOf = (i: number, count: number) => Math.floor((i * WAVES) / count);

/**
 * The bodies, the lost ones' pops and the magnitude arc, all drawn about the foot under one translate (the Track V
 * simplify: a camera pan moves one attribute, not every body). Each wave is a group: its walk offset one translate,
 * its drift one animation (an amorphous horde, V5; the stylesheet decides who drifts).
 */
export default function HordeBodies({ id, foot, g, horde, fill, title, still, shift }: Props) {
  const count = hordeBodyCount(horde.magnitude);
  const pops = usePops(count, still);
  const r = Math.max(1.5, g.rx * BODY_SHARE);
  const at = (p: Unit) => ({ x: p.x * g.rx, y: p.z * g.ry });
  const layout = hordeLayout(count);
  const ring = { cx: 0, cy: 0, rx: g.rx * ARC_OUT, ry: g.ry * ARC_OUT };
  const share = magnitudeShare(horde.magnitude, horde.max);
  return (
    <g
      data-testid={`token-body-${id}`}
      data-bodies={count}
      transform={`translate(${foot.x.toFixed(1)} ${foot.y.toFixed(1)})`}
      className={pops.length ? styles.hordeTightening : undefined}
    >
      <path
        className={styles.hordeArc}
        d={arcPath(ring, share)}
        data-testid="horde-magnitude-arc"
        data-share={share.toFixed(2)}
      />
      {Array.from({ length: WAVES }, (_, w) => (
        <g key={w} transform={shift?.[w] ? `translate(${shift[w].x.toFixed(1)} ${shift[w].y.toFixed(1)})` : undefined}>
          <g className={styles.hordeWave} style={{ animationDelay: `${-1.3 * w}s` }}>
            {layout.map((p, i) =>
              waveOf(i, count) === w ? <Body key={i} at={at(p)} kind={g.kind} r={r} fill={fill} /> : null,
            )}
          </g>
        </g>
      ))}
      {pops.map((p, i) => (
        <circle
          key={`${count}-${i}`}
          className={styles.hordePop}
          cx={at(p).x}
          cy={at(p).y}
          r={r * 2.2}
          data-testid="horde-pop"
        />
      ))}
      <title>{title}</title>
    </g>
  );
}

/** One body, in the figure's shape where it has one, else a circle (which tightens as the horde thins). */
function Body({
  at,
  kind,
  r,
  fill,
}: {
  at: { x: number; y: number };
  kind: MarkerGeometry['kind'];
  r: number;
  fill: string;
}) {
  const d = bodyShape(kind, at.x, at.y, r);
  if (d) return <path className={styles.tokenBody} d={d} fill={fill} />;
  return <circle className={`${styles.tokenBody} ${styles.hordeBody}`} cx={at.x} cy={at.y} r={r} fill={fill} />;
}

/**
 * The bodies a thinning horde just lost, where they stood in its layout before (unit places, so they follow the
 * camera), for POP_MS; none on a screen that holds still, and none the first time it is seen. A timer, not the pop's
 * own animationend: that never comes in a hidden tab, and a pop left standing keeps the bodies sliding.
 */
function usePops(count: number, still: boolean): readonly Unit[] {
  const was = useRef(count);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [pops, setPops] = useState<readonly Unit[]>([]);
  // The timer outlives the effect's re-runs (gotcha: a timer cleared in an effect's cleanup dies with the next run).
  useEffect(() => {
    const lost = was.current > count && !still ? hordeLayout(was.current).slice(count) : [];
    was.current = count;
    if (!lost.length) return;
    clearTimeout(timer.current);
    setPops(lost);
    timer.current = setTimeout(() => setPops([]), POP_MS);
  }, [count, still]);
  useEffect(() => {
    const t = timer;
    return () => clearTimeout(t.current);
  }, []);
  return pops;
}
